import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarBlank, MapPin, Airplane, Gift, Envelope, Users, ForkKnife } from '@phosphor-icons/react';

const API = '/api';
const WEDDING_DATE = new Date('2026-12-11T16:00:00');

// Fallback images in case API fails
const DEFAULT_HERO_IMAGES = [
  { url: 'https://customer-assets.emergentagent.com/job_2a6383c9-47ae-4f9d-80c4-df64c0760867/artifacts/g7c83ht6_IMG_7601.jpeg', position: '80% 40%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/gxx4pk44_IMG_0085.JPG', position: 'center 20%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_2a6383c9-47ae-4f9d-80c4-df64c0760867/artifacts/xkax76i6_IMG_7602.jpeg', position: 'center 30%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/2miso3sm_IMG_0393.JPG', position: 'center center', scale: '90%' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/4uq9fp6k_IMG_0132.jpg', position: 'center 30%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/qldqe7kw_IMG_0363.JPG', position: 'center 20%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/m11cgsrv_IMG_0378.JPG', position: 'center 25%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_2a6383c9-47ae-4f9d-80c4-df64c0760867/artifacts/xvd03hh7_IMG_6961.jpeg', position: '50% 100%', scale: 'cover' },
  { url: 'https://customer-assets.emergentagent.com/job_97bfecff-239b-4344-b43e-bd6560330dd8/artifacts/qt5rub6a_IMG_0257.JPG', position: 'center 25%', scale: 'cover' },
];

// Default content fallback
const DEFAULT_CONTENT = {
  couple_names: "Stefanie & Walter",
  wedding_date: "December 11, 2026",
  wedding_date_es: "11 de Diciembre, 2026",
  venue: "Cardedeu, Lake Coatepeque",
  venue_es: "Cardedeu, Lago de Coatepeque",
  country: "El Salvador",
  how_we_met_title: "How We Met",
  how_we_met_title_es: "Cómo Nos Conocimos",
  how_we_met: "Stefanie and Walter's story began years before either of them knew it would lead here...",
  how_we_met_es: "La historia de Stefanie y Walter comenzó años antes de que supieran que llegarían aquí...",
  proposal_title: "The Proposal",
  proposal_title_es: "La Propuesta",
  proposal: "On September 20th, Stefanie thought she was getting dressed up to celebrate someone else's love story...",
  proposal_es: "El 20 de septiembre, Stefanie pensó que se estaba arreglando para celebrar la historia de amor de alguien más...",
  our_forever_title: "Our Forever",
  our_forever_title_es: "Nuestro Para Siempre",
  our_forever: "Now we're ready to begin our next chapter surrounded by the people we love most.",
  our_forever_es: "Ahora estamos listos para comenzar nuestro próximo capítulo rodeados de las personas que más amamos."
};

function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const difference = WEDDING_DATE - new Date();
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div data-testid="countdown-timer" className="flex justify-center gap-6 md:gap-12">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <span className="font-display text-4xl md:text-6xl" style={{ color: '#B8A88A' }}>{value.toString().padStart(2, '0')}</span>
          <span className="block font-body text-xs md:text-sm uppercase tracking-widest text-stone-500 mt-2">{unit}</span>
        </div>
      ))}
    </div>
  );
}

export default function HomePage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [heroImages, setHeroImages] = useState(DEFAULT_HERO_IMAGES);
  const [content, setContent] = useState(DEFAULT_CONTENT);

  // Fetch slideshow images and content from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [imagesRes, contentRes] = await Promise.all([
          axios.get(`${API}/slideshow`),
          axios.get(`${API}/content/homepage`)
        ]);
        
        if (imagesRes.data && imagesRes.data.length > 0) {
          setHeroImages(imagesRes.data);
        }
        
        if (contentRes.data) {
          setContent(prev => ({ ...prev, ...contentRes.data }));
        }
      } catch (error) {
        console.log('Using default content');
      }
    };
    fetchData();
  }, []);

  // Rotate hero images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Navigate to specific image
  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div data-testid="home-page" className="bg-stone-50">
      {/* Hero Section with Rotating Background */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Images with Crossfade */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${image.url})`,
              backgroundSize: image.scale || 'cover',
              backgroundPosition: image.position,
              backgroundRepeat: 'no-repeat',
              opacity: index === currentImageIndex ? 1 : 0,
            }}
          />
        ))}
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-4">
          <p className="font-body text-sm md:text-base tracking-[0.3em] uppercase mb-1 animate-fade-in">
            We're Getting Married
          </p>
          <p className="font-body text-xs md:text-sm tracking-[0.2em] italic mb-6 animate-fade-in text-white/70">
            ¡Nos Casamos!
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6 animate-fade-in stagger-1">
            Stefanie <span className="italic" style={{ color: '#C9B99A' }}>&</span> Walter
          </h1>
          <p className="font-body text-lg md:text-xl tracking-wide mb-8 animate-fade-in stagger-2">
            December 11, 2026 • Coatapeque, El Salvador
          </p>
          <Link
            to="/rsvp"
            data-testid="hero-rsvp-btn"
            className="inline-block bg-white text-stone-900 px-10 py-4 font-body font-medium tracking-wide
                       transition-all duration-300 hover:scale-105 animate-fade-in stagger-3"
            style={{ 
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#C9B99A';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.color = '#1c1917';
            }}
          >
            <span>RSVP Now</span>
            <span className="mx-2 text-stone-300">|</span>
            <span className="italic text-stone-500">Confirmar</span>
          </Link>
        </div>

        {/* Dot Navigation */}
        <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2 md:gap-3">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                index === currentImageIndex 
                  ? 'bg-white scale-125' 
                  : 'bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
        
      </section>

      {/* Countdown Section */}
      <section data-testid="countdown-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: '#B8A88A' }}>
            Counting Down To
          </p>
          <p className="font-body text-xs tracking-[0.15em] italic text-stone-400 mb-4">
            Cuenta Regresiva
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-1">
            Our Special Day
          </h2>
          <p className="font-display text-lg md:text-xl italic text-stone-400 mb-12">
            Nuestro Día Especial
          </p>
          <CountdownTimer />
        </div>
      </section>

      {/* Our Story Section */}
      <section data-testid="our-story-section" className="py-20 md:py-32 bg-stone-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: '#B8A88A' }}>
              Our Journey
            </p>
            <p className="font-body text-xs tracking-[0.15em] italic text-stone-400 mb-4">
              Nuestro Camino
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-1">
              Our Story
            </h2>
            <p className="font-display text-lg italic text-stone-400">
              Nuestra Historia
            </p>
          </div>

          {/* Story Content - Dynamic from Admin */}
          <div className="space-y-12">
            <div className="text-center">
              <h3 className="font-display text-2xl text-stone-900 mb-2">{content.how_we_met_title || 'How We Met'}</h3>
              <p className="font-body text-sm italic text-stone-400 mb-4">{content.how_we_met_title_es || 'Cómo Nos Conocimos'}</p>
              {(content.how_we_met || '').split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="font-body text-stone-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              {content.how_we_met_es && (
                <p className="font-body text-sm italic text-stone-500 leading-relaxed">
                  {content.how_we_met_es}
                </p>
              )}
            </div>

            <div className="text-center">
              <h3 className="font-display text-2xl text-stone-900 mb-2">{content.proposal_title || 'The Proposal'}</h3>
              <p className="font-body text-sm italic text-stone-400 mb-4">{content.proposal_title_es || 'La Propuesta'}</p>
              {(content.proposal || '').split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="font-body text-stone-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              {content.proposal_es && (
                <p className="font-body text-sm italic text-stone-500 leading-relaxed">
                  {content.proposal_es}
                </p>
              )}
            </div>

            <div className="text-center">
              <h3 className="font-display text-2xl text-stone-900 mb-2">{content.our_forever_title || 'Our Forever'}</h3>
              <p className="font-body text-sm italic text-stone-400 mb-4">{content.our_forever_title_es || 'Nuestro Para Siempre'}</p>
              <p className="font-body text-stone-600 leading-relaxed mb-4">
                {content.our_forever || "Now we're ready to begin our next chapter surrounded by the people we love most."}
              </p>
              {content.our_forever_es && (
                <p className="font-body text-sm italic text-stone-500 leading-relaxed">
                  {content.our_forever_es}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section data-testid="welcome-section" className="py-20 md:py-32 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="decorative-line mx-auto mb-8" />
          <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-6">
            Join Us in Paradise
          </h2>
          <p className="font-body text-stone-600 leading-relaxed max-w-2xl mx-auto mb-8">
            We invite you to celebrate our love at a beautiful destination wedding
            in Coatapeque, El Salvador. Surrounded by stunning volcanic lakes and
            lush tropical landscapes, we'll exchange our vows and begin our forever
            together with those we cherish most.
          </p>
          <p className="font-body text-stone-600 leading-relaxed max-w-2xl mx-auto">
            We can't wait to share this magical moment with you. Your presence
            would mean the world to us as we embark on this beautiful journey.
          </p>
        </div>
      </section>

      {/* Quick Links Grid */}
      <section data-testid="quick-links-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: '#B8A88A' }}>
              Everything You Need
            </p>
            <p className="font-body text-xs tracking-[0.15em] italic text-stone-400 mb-4">
              Todo Lo Que Necesitas
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-1">
              Wedding Information
            </h2>
            <p className="font-display text-lg italic text-stone-400">
              Información de la Boda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Itinerary Card */}
            <Link
              to="/itinerary"
              data-testid="quick-link-itinerary"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <CalendarBlank size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                Itinerary
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Itinerario</p>
              <p className="font-body text-sm text-stone-500">
                View the wedding weekend schedule
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Programa del fin de semana
              </p>
            </Link>

            {/* Accommodations Card */}
            <Link
              to="/accommodations"
              data-testid="quick-link-accommodations"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <svg className="w-8 h-8 text-[#B8A88A] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                Stay
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Hospedaje</p>
              <p className="font-body text-sm text-stone-500">
                Find the perfect place to stay
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Encuentra dónde hospedarte
              </p>
            </Link>

            {/* Travel Card */}
            <Link
              to="/travel"
              data-testid="quick-link-travel"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <MapPin size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                Travel Tips
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Consejos de Viaje</p>
              <p className="font-body text-sm text-stone-500">
                Everything about visiting El Salvador
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Todo sobre visitar El Salvador
              </p>
            </Link>

            {/* RSVP Card */}
            <Link
              to="/rsvp"
              data-testid="quick-link-rsvp"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <Envelope size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                RSVP
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Confirmar Asistencia</p>
              <p className="font-body text-sm text-stone-500">
                Let us know if you'll be joining us
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Déjanos saber si nos acompañarás
              </p>
            </Link>

            {/* Flights Card */}
            <Link
              to="/flights"
              data-testid="quick-link-flights"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <Airplane size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                Flight Info
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Información de Vuelo</p>
              <p className="font-body text-sm text-stone-500">
                Share your travel details
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Comparte tus detalles de viaje
              </p>
            </Link>

            {/* Registry Card */}
            <Link
              to="/registry"
              data-testid="quick-link-registry"
              className="group p-8 bg-stone-50 border border-stone-200 transition-all duration-300
                         hover:border-[#C9B99A] hover:shadow-lg"
            >
              <Gift size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-xl text-stone-900 mb-1 group-hover:text-[#B8A88A] transition-colors">
                Registry
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Regalos</p>
              <p className="font-body text-sm text-stone-500">
                Your presence is our present
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-1">
                Tu presencia es nuestro regalo
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section
        data-testid="location-section"
        className="relative py-32 md:py-48"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_coatapeque-wedding/artifacts/mhrffcit_IMG_6086.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: '#C9B99A' }}>
            Our Venue
          </p>
          <p className="font-body text-xs tracking-[0.15em] italic mb-4 text-white/60">
            Nuestro Lugar
          </p>
          <h2 className="font-display text-4xl md:text-5xl mb-6">
            Cardedeu, Lake Coatapeque
          </h2>
          <p className="font-body text-lg text-white/80 max-w-2xl mx-auto mb-4">
            Our celebration will be held at Cardedeu, a stunning lakeside venue 
            overlooking Lake Coatapeque in El Salvador.
          </p>
          <p className="font-body text-base italic text-white/60 max-w-2xl mx-auto">
            Nuestra celebración será en Cardedeu, un hermoso lugar junto al lago 
            con vistas al Lago de Coatepeque en El Salvador.
          </p>
        </div>
      </section>
    </div>
  );
}
